import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { CoverImageUpload } from "@/components/ImageUpload/CoverImageUpload";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { useWebStreamConfigForm } from "@/pages/channels/hooks/form/useWebStreamConfigForm";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { useUploadImageMutation } from "@/resources/attachment/useUploadImageMutation";

type WebStreamConfig = InterludeTypes["WebStreamConfig"];

const styles = {
  section: {
    backgroundColor: theme.colors.white,
    borderRadius: "8px",
  },
  colorFieldsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
  },
  colorField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  colorPickerWrapper: {
    width: "100%",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const HeroAndColorForm: React.FC = function HeroAndColorForm() {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<WebStreamConfig>();
  const { channel } = ChannelQueriesContext.useData();
  const heroInputRef = useRef<HTMLInputElement>(null);
  const externalChannelId =
    channel.type === "wccs" && channel.externalChannelId
      ? channel.externalChannelId
      : "";
  const heroUploadMutation = useUploadImageMutation({
    onSuccess: (url: string) => {
      setValue("menu.heroImageUrl", url, { shouldDirty: true });
    },
    onSettled: () => {
      if (heroInputRef.current) {
        heroInputRef.current.value = "";
      }
    },
  });

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography variant="h3" fontWeight={500}>
          {t("webStreamConfig.hero.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack sx={styles.section} gap="16px">
          <Stack gap="8px">
            <Typography variant="h3" fontWeight={500}>
              {t("webStreamConfig.hero.image.title")}
            </Typography>
            <Typography variant="note">
              {t("webStreamConfig.hero.image.requirements")}
            </Typography>
            <Controller
              name="menu.heroImageUrl"
              control={control}
              render={({ field }) => (
                <CoverImageUpload
                  inputRef={heroInputRef}
                  value={field.value}
                  onChange={(file) => {
                    if (!externalChannelId) return;
                    heroUploadMutation.mutate({
                      feature: "webStreamHero",
                      dialogueId: externalChannelId,
                      file,
                    });
                  }}
                  onClear={() => {
                    setValue(
                      "menu.heroImageUrl",
                      useWebStreamConfigForm.defaultWebStreamConfig.menu
                        .heroImageUrl,
                      { shouldDirty: true },
                    );
                  }}
                  disable={heroUploadMutation.isPending}
                />
              )}
            />
          </Stack>
          <Stack gap="8px">
            <Typography variant="h3" fontWeight={500}>
              {t("webStreamConfig.hero.colors.title")}
            </Typography>
            <Box sx={styles.colorFieldsGrid}>
              <Box sx={styles.colorField}>
                <Typography variant="body">
                  {t("webStreamConfig.hero.colors.primaryColor")}
                </Typography>
                <Controller
                  name="menu.colors.primaryColor"
                  control={control}
                  render={({ field }) => (
                    <Box sx={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={
                          field.value ??
                          useWebStreamConfigForm.defaultWebStreamConfig.menu
                            .colors.primaryColor
                        }
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebStreamConfigForm.defaultWebStreamConfig.menu
                            .colors.primaryColor
                        }
                      />
                    </Box>
                  )}
                />
              </Box>
              <Box sx={styles.colorField}>
                <Typography variant="body">
                  {t("webStreamConfig.hero.colors.secondaryBackgroundColor")}
                </Typography>
                <Controller
                  name="menu.colors.secondaryBackgroundColor"
                  control={control}
                  render={({ field }) => (
                    <Box sx={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={
                          field.value ??
                          useWebStreamConfigForm.defaultWebStreamConfig.menu
                            .colors.secondaryBackgroundColor
                        }
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebStreamConfigForm.defaultWebStreamConfig.menu
                            .colors.secondaryBackgroundColor
                        }
                      />
                    </Box>
                  )}
                />
              </Box>
              <Box sx={styles.colorField}>
                <Typography variant="body">
                  {t("webStreamConfig.hero.colors.unreadColor")}
                </Typography>
                <Controller
                  name="menu.colors.unreadColor"
                  control={control}
                  render={({ field }) => (
                    <Box sx={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={
                          field.value ??
                          useWebStreamConfigForm.defaultWebStreamConfig.menu
                            .colors.unreadColor
                        }
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebStreamConfigForm.defaultWebStreamConfig.menu
                            .colors.unreadColor
                        }
                      />
                    </Box>
                  )}
                />
              </Box>
            </Box>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
